// Admin MAX-BID store, one per LOT, handed out by the ref-counting registry the
// `MaxBidsProvider` owns (`./MaxBidsProvider.tsx` + `./entity-state-registry.ts`).
// BL-1325; renamed off `prebid` by BUG-1426; scoped from the auction to the lot
// by BL-1552.
//
// The max-bid-shaped BINDING of the generic engine in `./entity-state-store.ts`
// — it supplies the four per-stream details (`maxBidId` as the fold key, the
// `sequence` ordering key, the `maxbid.delta` event, and the catch-up response's
// `maxBids` array) and nothing else. Sibling of `registration-state-store.ts`; the
// LWW fold, subscription lifecycle, cold-seed + reconcile catch-up, and connection
// tracking all live in the shared engine.
//
// This binding is still exactly the four per-stream details, unchanged in count by
// BL-1629: gap detection lives in the engine and is switched on by the PAYLOAD
// carrying a `sequence`, not by anything declared here. That is BL-1628 AC 9's
// actual test, and it is why converting this stream was a rename rather than a
// build.

import type {
    AdminMaxBidDelta,
    MaxBidStateCatchupResult,
} from '../../mappers/realtime/admin-max-bids';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import {
    DISCONNECTED_ENTITY_STATE,
    EntityStateStore,
    type EntityStateConnection,
    type NotifyScheduler,
} from './entity-state-store';

// The catch-up result is declared once, beside the delta it carries, so the
// route registry can type the response without importing this client store
// (BL-2010, ADR 027 — Response Contract).
export type { MaxBidStateCatchupResult };

/**
 * Fetches one page of max-bid state from the authenticated per-lot admin max-bid
 * catch-up route. `sinceSequence === 0` is the cold-seed (every `max_bids` row on
 * the LOT). The app injects this via the provider — the topic is minted
 * server-side and the client never derives it.
 *
 * The scope parameter is a LOT id since BL-1552. The name stays generic because
 * the store is scope-agnostic: it passes whatever it was constructed with
 * straight through.
 */
export type MaxBidStateCatchupFetcher = (
    lotId: string,
    sinceSequence: number,
) => Promise<MaxBidStateCatchupResult>;

/** The max-bid stream's binding of the engine's four per-stream details. */
const MAX_BID_STATE_CONFIG = {
    keyOf: (delta: AdminMaxBidDelta) => delta.maxBidId,
    cursorOf: (delta: AdminMaxBidDelta) => delta.sequence,
    event: 'maxbid.delta' as const,
    itemsOf: (result: MaxBidStateCatchupResult) => result.maxBids,
    // One delta per frame — this stream was not batched by BUG-1517. If it ever
    // is, only this line changes.
    deltasOf: (payload: unknown) => [(payload as { state: AdminMaxBidDelta }).state],
};

/**
 * Whether the max-bid stream is established. Consumers render a connection
 * banner from this — a COUNT can't stand in for it, since a lot may
 * legitimately have zero standing maximums.
 */
export type MaxBidStateConnection = EntityStateConnection;

/** Pre-connection default, shared so consumers don't each invent one. */
export const DISCONNECTED_MAX_BID_STATE: MaxBidStateConnection =
    DISCONNECTED_ENTITY_STATE;

/**
 * The admin max-bid store for ONE LOT. A thin max-bid-shaped facade over
 * `EntityStateStore` — `getMaxBids()` is the domain name for the engine's entity
 * map.
 *
 * The feed is per-lot since BL-1552: one topic per lot, subscribed when the clerk
 * selects it and dropped when they move on. It was auction-wide, which meant a
 * 5.7 MB cold seed at 2,500 lots to serve a panel about one of them. Instances
 * are shared and ref-counted by `EntityStateStoreRegistry`, so two components on
 * one lot cost one subscription.
 */
export class MaxBidStateStore extends EntityStateStore<
    AdminMaxBidDelta,
    MaxBidStateCatchupResult
> {
    constructor(
        subscriber: RealtimeSubscriber,
        fetcher: MaxBidStateCatchupFetcher,
        /** The LOT this store is scoped to (BL-1552) — not an auction. */
        lotId: string,
        /**
         * Override the coalesced-notify scheduler (BUG-1407). Tests inject a
         * manual pump; production takes the default short-timer window.
         */
        scheduleNotify?: NotifyScheduler,
    ) {
        super(subscriber, fetcher, lotId, MAX_BID_STATE_CONFIG, scheduleNotify);
    }

    /** Current LWW-folded state of every known `max_bids` row, keyed by `maxBidId`. */
    getMaxBids(): ReadonlyMap<string, AdminMaxBidDelta> {
        return this.getEntities();
    }
}
